import {z} from "zod";

/** Habit loop contracts shared by the API, the web app, and the native widgets. */

export const petStages = ["seed", "sprout", "sapling", "bush", "tree", "bloom", "ancient"] as const;
export type PetStage = (typeof petStages)[number];
/** XP needed to reach each stage. */
export const petStageThresholds: Readonly<Record<PetStage, number>> = {seed: 0, sprout: 40, sapling: 150, bush: 400, tree: 900, bloom: 1800, ancient: 3600};
export const petStageNames: Readonly<Record<PetStage, string>> = {seed: "Seed", sprout: "Sprout", sapling: "Sapling", bush: "Bush", tree: "Tree", bloom: "In bloom", ancient: "Ancient oak"};

export const petMoods = ["thriving", "happy", "thirsty", "wilting"] as const;
export type PetMood = (typeof petMoods)[number];

export const checkInMoods = ["great", "okay", "rough"] as const;
export type CheckInMood = (typeof checkInMoods)[number];
export const checkInSources = ["app", "widget", "notification", "intent", "control"] as const;
export type CheckInSource = (typeof checkInSources)[number];

export const engagementEventKinds = ["question_asked", "share_completed", "wrapped_viewed", "roast_viewed", "recap_viewed", "transaction_viewed", "category_viewed", "garden_viewed", "invite_shared", "widget_installed", "live_activity_started", "notification_opened", "app_icon_changed", "bank_connected"] as const;
export type EngagementEventKind = (typeof engagementEventKinds)[number];

export const xpRewards = {checkIn: 10, streakBonusPerWeek: 5, question: 3, share: 15, inviteAccepted: 100, bankConnected: 50} as const;

/** Calendar day in the person's time zone, `YYYY-MM-DD`. */
export const localDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export type LocalDay = z.infer<typeof localDaySchema>;

export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone || timeZone.length > 64) return false;
  try {
    new Intl.DateTimeFormat("en-US", {timeZone});
    return true;
  } catch {
    return false;
  }
}

export function localDay(at: Date, timeZone: string): LocalDay {
  const parts = new Intl.DateTimeFormat("en-CA", {timeZone: isValidTimeZone(timeZone) ? timeZone : "UTC", year: "numeric", month: "2-digit", day: "2-digit"}).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "00";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Hour (0–23) on the person's clock. */
export function localHour(at: Date, timeZone: string): number {
  const hour = new Intl.DateTimeFormat("en-US", {timeZone: isValidTimeZone(timeZone) ? timeZone : "UTC", hour: "numeric", hourCycle: "h23"}).format(at);
  return Number.parseInt(hour, 10) % 24;
}

const dayMs = 86_400_000;
function dayNumber(day: LocalDay): number {
  return Math.round(Date.parse(`${day}T00:00:00Z`) / dayMs);
}
/** Whole days from `from` to `to`; positive when `to` is later. */
export function daysBetween(from: LocalDay, to: LocalDay): number {
  return dayNumber(to) - dayNumber(from);
}
export function addDays(day: LocalDay, amount: number): LocalDay {
  return new Date((dayNumber(day) + amount) * dayMs).toISOString().slice(0, 10);
}

export function petStageForXp(xp: number): PetStage {
  let stage: PetStage = "seed";
  for (const candidate of petStages) if (xp >= petStageThresholds[candidate]) stage = candidate;
  return stage;
}

export interface PetProgress {
  stage: PetStage;
  next: PetStage | null;
  /** XP earned inside the current stage, and the span to the next one (null at the final stage). */
  into: number;
  span: number | null;
}
export function petProgress(xp: number): PetProgress {
  const stage = petStageForXp(xp);
  const next = petStages[petStages.indexOf(stage) + 1] ?? null;
  const floor = petStageThresholds[stage];
  return {stage, next, into: Math.max(0, xp - floor), span: next ? petStageThresholds[next] - floor : null};
}

/** Level n starts at 25·n·(n−1) XP: 0, 50, 150, 300, 500… */
export function levelForXp(xp: number): number {
  let level = 1;
  while (25 * (level + 1) * level <= xp) level += 1;
  return level;
}

/** The pet reflects how recently the person checked in, never how much they spent. */
export function petMoodFor(lastCheckInDay: LocalDay | null, today: LocalDay): PetMood {
  if (!lastCheckInDay) return "thirsty";
  const gap = daysBetween(lastCheckInDay, today);
  if (gap <= 0) return "thriving";
  if (gap === 1) return "happy";
  if (gap === 2) return "thirsty";
  return "wilting";
}

export const petMoodExpressions = {thriving: "excited", happy: "happy", thirsty: "sad", wilting: "crying"} as const satisfies Record<PetMood, string>;

export interface QuestDefinition {
  id: string;
  title: string;
  detail: string;
  /** `check_in` counts check-ins; every other quest counts one engagement event kind. */
  kind: EngagementEventKind | "check_in";
  target: number;
  xp: number;
  icon: "droplets" | "message-circle" | "tag" | "receipt" | "share" | "gift" | "flame" | "sprout" | "users";
  /** Route that completes the quest. */
  to: string;
}

export const questCatalog: readonly QuestDefinition[] = [
  {id: "check_in", title: "Water Sprout", detail: "Do today’s Money Minute", kind: "check_in", target: 1, xp: 10, icon: "droplets", to: "/checkin"},
  {id: "ask", title: "Ask Sprout a question", detail: "Anything about your money", kind: "question_asked", target: 1, xp: 5, icon: "message-circle", to: "/chat?focus=1"},
  {id: "category", title: "Inspect a category", detail: "See where one slice of spending went", kind: "category_viewed", target: 1, xp: 5, icon: "tag", to: "/insights"},
  {id: "transaction", title: "Review your purchases", detail: "Open your spending records", kind: "transaction_viewed", target: 1, xp: 5, icon: "receipt", to: "/insights/transactions"},
  {id: "wrapped", title: "Open your Wrapped", detail: "Your week in money", kind: "wrapped_viewed", target: 1, xp: 5, icon: "gift", to: "/wrapped/week"},
  {id: "roast", title: "Get roasted", detail: "Let Sprout judge your spending", kind: "roast_viewed", target: 1, xp: 5, icon: "flame", to: "/roast"},
  {id: "share", title: "Share a Sprout card", detail: "Show your streak or your Wrapped", kind: "share_completed", target: 1, xp: 15, icon: "share", to: "/garden"},
  {id: "invite", title: "Invite a friend", detail: "You both get rewards", kind: "invite_shared", target: 1, xp: 15, icon: "users", to: "/invite"},
  {id: "garden", title: "Visit your garden", detail: "Check on Sprout’s growth", kind: "garden_viewed", target: 1, xp: 3, icon: "sprout", to: "/garden"}
];

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

/** Three quests per person per day: the check-in plus two stable picks. */
export function dailyQuestIds(userId: string, day: LocalDay): string[] {
  const pool = questCatalog.filter((quest) => quest.id !== "check_in");
  const picks: string[] = [];
  let seed = hash(`${userId}:${day}`);
  while (picks.length < 2 && pool.length > 0) {
    const quest = pool.splice(seed % pool.length, 1)[0];
    if (quest) picks.push(quest.id);
    seed = hash(`${seed}`);
  }
  return ["check_in", ...picks];
}

export const appIconIds = ["classic", "midnight", "mono", "gold", "bloom", "ocean", "ember"] as const;
export type AppIconId = (typeof appIconIds)[number];

export interface AchievementDefinition {
  id: string;
  title: string;
  description: string;
  emoji: string;
  xp: number;
  /** Alternate app icon this achievement unlocks. */
  unlocksIcon?: AppIconId;
}

export const achievementCatalog: readonly AchievementDefinition[] = [
  {id: "first_check_in", title: "First sip", description: "Finish your first Money Minute", emoji: "💧", xp: 10},
  {id: "streak_3", title: "On a roll", description: "Check in 3 days in a row", emoji: "🔥", xp: 15},
  {id: "streak_7", title: "Week warrior", description: "Check in 7 days in a row", emoji: "🗓️", xp: 30, unlocksIcon: "mono"},
  {id: "streak_14", title: "Fortnight of focus", description: "Check in 14 days in a row", emoji: "🎯", xp: 50},
  {id: "streak_30", title: "Golden habit", description: "Check in 30 days in a row", emoji: "🏆", xp: 100, unlocksIcon: "gold"},
  {id: "streak_100", title: "Centurion", description: "Check in 100 days in a row", emoji: "💯", xp: 300},
  {id: "streak_365", title: "Evergreen", description: "Check in every day for a year", emoji: "🌳", xp: 1000},
  {id: "bank_connected", title: "Plugged in", description: "Connect your first bank", emoji: "🔌", xp: 50},
  {id: "questions_10", title: "Curious mind", description: "Ask Sprout 10 questions", emoji: "🧠", xp: 30},
  {id: "questions_50", title: "Money detective", description: "Ask Sprout 50 questions", emoji: "🕵️", xp: 100},
  {id: "first_share", title: "Show-off", description: "Share your first Sprout card", emoji: "📸", xp: 15},
  {id: "first_invite", title: "Gardener", description: "A friend you invited started Sprout", emoji: "🌊", xp: 100, unlocksIcon: "ocean"},
  {id: "roasted", title: "Well done", description: "Survive your first Spending Roast", emoji: "🌶️", xp: 15, unlocksIcon: "ember"},
  {id: "wrapped", title: "That’s a wrap", description: "Open your first Wrapped", emoji: "🎁", xp: 15},
  {id: "night_owl", title: "Night owl", description: "Check in after 11 pm", emoji: "🦉", xp: 10, unlocksIcon: "midnight"},
  {id: "early_bird", title: "Early bird", description: "Check in before 7 am", emoji: "🐦", xp: 10},
  {id: "bloom", title: "Full bloom", description: "Grow Sprout to bloom", emoji: "🌸", xp: 50, unlocksIcon: "bloom"},
  {id: "level_10", title: "Seasoned", description: "Reach level 10", emoji: "⭐", xp: 50},
  {id: "widget", title: "Home screen hero", description: "Add a Sprout widget", emoji: "📱", xp: 20},
  {id: "live_activity", title: "Live and direct", description: "Pin today’s spending to your Lock Screen", emoji: "📍", xp: 20}
];

export const appIconCatalog: ReadonlyArray<{id: AppIconId; name: string; /** Native alternate icon name; null is the primary icon. */ nativeName: string | null; unlockedBy: string | null}> = [
  {id: "classic", name: "Classic", nativeName: null, unlockedBy: null},
  {id: "midnight", name: "Midnight", nativeName: "AppIcon-Midnight", unlockedBy: "night_owl"},
  {id: "mono", name: "Mono", nativeName: "AppIcon-Mono", unlockedBy: "streak_7"},
  {id: "gold", name: "Gold", nativeName: "AppIcon-Gold", unlockedBy: "streak_30"},
  {id: "bloom", name: "Bloom", nativeName: "AppIcon-Bloom", unlockedBy: "bloom"},
  {id: "ocean", name: "Ocean", nativeName: "AppIcon-Ocean", unlockedBy: "first_invite"},
  {id: "ember", name: "Ember", nativeName: "AppIcon-Ember", unlockedBy: "roasted"}
];

/** Streak lengths that celebrate, prompt sharing, and may present the paywall. */
export const streakMilestones = [3, 7, 14, 30, 50, 100, 200, 365] as const;
export function isStreakMilestone(streak: number): boolean {
  return (streakMilestones as readonly number[]).includes(streak);
}
export function nextStreakMilestone(streak: number): number | null {
  return streakMilestones.find((milestone) => milestone > streak) ?? null;
}

export const subscriptionStatuses = ["none", "trial", "active", "expired"] as const;
export type SubscriptionStatus = (typeof subscriptionStatuses)[number];

export const engagementPreferencesSchema = z.object({
  timeZone: z.string().min(1).max(64).refine(isValidTimeZone, "Unknown time zone"),
  recapHour: z.number().int().min(0).max(23),
  notifyDaily: z.boolean(),
  notifyStreak: z.boolean(),
  notifyTransactions: z.boolean(),
  notifyWeekly: z.boolean(),
  /** Explicit consent for offers and promotional reminders (App Review 4.5.4). */
  notifyOffers: z.boolean(),
  /** Start the “Today’s spending” Live Activity every morning via push-to-start. */
  liveActivityDaily: z.boolean(),
  appIcon: z.enum(appIconIds)
});
export type EngagementPreferences = z.infer<typeof engagementPreferencesSchema>;
export const engagementPreferencesUpdateSchema = engagementPreferencesSchema.partial();
export type EngagementPreferencesUpdate = z.infer<typeof engagementPreferencesUpdateSchema>;

export const defaultEngagementPreferences: EngagementPreferences = {
  timeZone: "America/New_York",
  recapHour: 19,
  notifyDaily: true,
  notifyStreak: true,
  notifyTransactions: true,
  notifyWeekly: true,
  notifyOffers: false,
  liveActivityDaily: false,
  appIcon: "classic"
};

export const questStateSchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string(),
  icon: z.string(),
  to: z.string(),
  xp: z.number().int(),
  progress: z.number().int().nonnegative(),
  target: z.number().int().positive(),
  done: z.boolean()
});
export type QuestState = z.infer<typeof questStateSchema>;

export const engagementStateSchema = z.object({
  today: localDaySchema,
  streak: z.object({current: z.number().int().nonnegative(), longest: z.number().int().nonnegative(), freezes: z.number().int().min(0).max(2), lastDay: localDaySchema.nullable(), checkedInToday: z.boolean(), nextMilestone: z.number().int().nullable()}),
  pet: z.object({stage: z.enum(petStages), mood: z.enum(petMoods), xp: z.number().int().nonnegative(), level: z.number().int().positive(), next: z.enum(petStages).nullable(), into: z.number().int().nonnegative(), span: z.number().int().positive().nullable()}),
  quests: z.array(questStateSchema),
  achievements: z.array(z.object({id: z.string(), unlockedAt: z.string().datetime()})),
  counts: z.object({checkIns: z.number().int().nonnegative(), questions: z.number().int().nonnegative(), shares: z.number().int().nonnegative(), invitesAccepted: z.number().int().nonnegative()}),
  preferences: engagementPreferencesSchema,
  referralCode: z.string(),
  subscription: z.enum(subscriptionStatuses),
  /** The server can reach this person with APNs; on-device reminders then step aside. */
  serverPush: z.boolean()
});
export type EngagementState = z.infer<typeof engagementStateSchema>;

export const engagementRewardsSchema = z.object({
  xpGained: z.number().int().nonnegative(),
  streakExtended: z.boolean(),
  freezesUsed: z.number().int().nonnegative(),
  questsCompleted: z.array(z.string()),
  achievementsUnlocked: z.array(z.string()),
  stageUp: z.enum(petStages).nullable(),
  levelUp: z.number().int().positive().nullable()
});
export type EngagementRewards = z.infer<typeof engagementRewardsSchema>;
export const noRewards: EngagementRewards = {xpGained: 0, streakExtended: false, freezesUsed: 0, questsCompleted: [], achievementsUnlocked: [], stageUp: null, levelUp: null};

export const checkInInputSchema = z.object({mood: z.enum(checkInMoods), source: z.enum(checkInSources).default("app"), timeZone: z.string().max(64).optional()});
export type CheckInInput = z.input<typeof checkInInputSchema>;
export const checkInResultSchema = z.object({state: engagementStateSchema, rewards: engagementRewardsSchema, alreadyCheckedIn: z.boolean()});
export type CheckInResult = z.infer<typeof checkInResultSchema>;

export const engagementEventInputSchema = z.object({kind: z.enum(engagementEventKinds), timeZone: z.string().max(64).optional()});
export type EngagementEventInput = z.infer<typeof engagementEventInputSchema>;
export const engagementEventResultSchema = z.object({state: engagementStateSchema, rewards: engagementRewardsSchema});
export type EngagementEventResult = z.infer<typeof engagementEventResultSchema>;

const minor = z.number().int();
export const merchantAmountSchema = z.object({label: z.string(), amountMinor: minor});

/** Amounts are integer minor units: totals positive, single transactions keep their sign. `deltaPct` is whole percentage points; `share` is a 0–1 fraction. */
export const dailyRecapSchema = z.object({
  day: localDaySchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  sample: z.boolean(),
  hasData: z.boolean(),
  yesterday: z.object({day: localDaySchema, spentMinor: minor, count: z.number().int().nonnegative(), typicalDayMinor: minor, deltaPct: z.number().nullable(), biggest: z.object({label: z.string(), amountMinor: minor, category: z.string()}).nullable()}),
  today: z.object({spentMinor: minor, count: z.number().int().nonnegative()}),
  month: z.object({spentMinor: minor, projectedMinor: minor, previousMonthMinor: minor.nullable(), dayOfMonth: z.number().int().positive(), daysInMonth: z.number().int().positive()}),
  upcoming: z.array(z.object({label: z.string(), amountMinor: minor, expectedOn: localDaySchema, cadence: z.string()})).max(5),
  topCategory: merchantAmountSchema.nullable()
});
export type DailyRecap = z.infer<typeof dailyRecapSchema>;

export const wrappedPeriods = ["week", "month"] as const;
export type WrappedPeriod = (typeof wrappedPeriods)[number];
export const wrappedSchema = z.object({
  period: z.enum(wrappedPeriods),
  label: z.string(),
  from: localDaySchema,
  to: localDaySchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  sample: z.boolean(),
  hasData: z.boolean(),
  totalSpentMinor: minor,
  previousSpentMinor: minor.nullable(),
  deltaPct: z.number().nullable(),
  incomeMinor: minor,
  transactionCount: z.number().int().nonnegative(),
  merchantCount: z.number().int().nonnegative(),
  noSpendDays: z.number().int().nonnegative(),
  topCategories: z.array(z.object({label: z.string(), amountMinor: minor, count: z.number().int().nonnegative(), share: z.number().min(0).max(1)})).max(5),
  topMerchants: z.array(z.object({label: z.string(), amountMinor: minor, count: z.number().int().nonnegative()})).max(5),
  /** `postedAt` is the purchase day at UTC midnight; format it in UTC. */
  biggestPurchase: z.object({label: z.string(), amountMinor: minor, postedAt: z.string().datetime()}).nullable(),
  busiestWeekday: z.object({weekday: z.number().int().min(0).max(6), label: z.string(), amountMinor: minor}).nullable(),
  personality: z.string(),
  funFacts: z.array(z.string()).max(6)
});
export type Wrapped = z.infer<typeof wrappedSchema>;

export const roastSchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  sample: z.boolean(),
  hasData: z.boolean(),
  /** 1 (gentle) to 5 (scorched). */
  heat: z.number().int().min(1).max(5),
  headline: z.string(),
  lines: z.array(z.object({emoji: z.string(), text: z.string()})).max(8),
  verdict: z.string()
});
export type Roast = z.infer<typeof roastSchema>;

export const widgetSnapshotSchema = z.object({
  version: z.literal(1),
  generatedAt: z.string().datetime(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  spentTodayMinor: minor,
  typicalDayMinor: minor,
  monthSpentMinor: minor,
  monthProjectedMinor: minor,
  previousMonthMinor: minor.nullable(),
  todayCount: z.number().int().nonnegative(),
  lastTransaction: z.object({label: z.string(), amountMinor: minor, postedAt: z.string().datetime()}).nullable(),
  topCategories: z.array(merchantAmountSchema).max(4),
  streak: z.number().int().nonnegative(),
  checkedInToday: z.boolean(),
  petStage: z.enum(petStages),
  petMood: z.enum(petMoods),
  xp: z.number().int().nonnegative(),
  level: z.number().int().positive(),
  preview: z.boolean(),
  sample: z.boolean()
});
export type WidgetSnapshot = z.infer<typeof widgetSnapshotSchema>;

/** Six unambiguous characters (no 0/O/1/I). */
export const referralCodePattern = /^[A-HJ-NP-Z2-9]{6}$/;
export function normalizeReferralCode(value: string): string | null {
  const code = value
    .trim()
    .toUpperCase()
    .replace(/^SPROUT[-\s]?/, "")
    .replace(/[\s-]/g, "");
  return referralCodePattern.test(code) ? code : null;
}
/** Universal link on the iPhone app's domain; without the app installed it opens an App Store hand-off page. */
export const referralLinkBase = "https://app-sprout.z4k.app/i/";
export const referralLink = (code: string) => `${referralLinkBase}${code}`;

export const referralApplySchema = z.object({code: z.string().trim().min(4).max(32)});
export const referralStateSchema = z.object({
  code: z.string(),
  link: z.string().url(),
  invited: z.number().int().nonnegative(),
  trials: z.number().int().nonnegative(),
  appliedCode: z.string().nullable(),
  /** Apple offer code granted as a referral reward, ready to redeem. */
  offerCode: z.string().nullable()
});
export type ReferralState = z.infer<typeof referralStateSchema>;

export const pushEnvironments = ["sandbox", "production"] as const;
export const deviceRegistrationSchema = z.object({
  token: z.string().regex(/^[0-9a-fA-F]{32,200}$/),
  environment: z.enum(pushEnvironments),
  appVersion: z.string().max(32).optional(),
  locale: z.string().max(35).optional(),
  timeZone: z.string().max(64).optional()
});
export type DeviceRegistration = z.infer<typeof deviceRegistrationSchema>;
export const deviceRemovalSchema = z.object({token: z.string().regex(/^[0-9a-fA-F]{32,200}$/)});

export const liveActivityTokenKinds = ["update", "push_to_start"] as const;
export const liveActivityRegistrationSchema = z.object({
  kind: z.enum(liveActivityTokenKinds),
  token: z.string().regex(/^[0-9a-fA-F]{32,400}$/),
  activityId: z.string().max(128).optional(),
  environment: z.enum(pushEnvironments)
});
export type LiveActivityRegistration = z.infer<typeof liveActivityRegistrationSchema>;
export const liveActivityEndSchema = z.object({activityId: z.string().min(1).max(128)});

/** Content state of the “Today’s spending” Live Activity; mirrors the Swift `ContentState`. */
export const dailyActivityStateSchema = z.object({
  spentTodayMinor: minor,
  typicalDayMinor: minor,
  currency: z.string().regex(/^[A-Z]{3}$/),
  todayCount: z.number().int().nonnegative(),
  lastLabel: z.string().max(60).nullable(),
  lastAmountMinor: minor.nullable(),
  streak: z.number().int().nonnegative(),
  petMood: z.enum(petMoods),
  updatedAt: z.string().datetime()
});
export type DailyActivityState = z.infer<typeof dailyActivityStateSchema>;

export interface EngagementStateResponse {
  data: EngagementState;
}
export interface CheckInResponse {
  data: CheckInResult;
}
export interface EngagementEventResponse {
  data: EngagementEventResult;
}
export interface DailyRecapResponse {
  data: DailyRecap;
}
export interface WrappedResponse {
  data: Wrapped;
}
export interface RoastResponse {
  data: Roast;
}
export interface WidgetSnapshotResponse {
  data: WidgetSnapshot;
}
export interface WidgetTokenResponse {
  data: {token: string};
}
export interface ReferralStateResponse {
  data: ReferralState;
}
export interface OkResponse {
  data: {ok: true};
}
